import { isBackendConfigured } from './client';
import type { AiChatAttachment } from './types';

/**
 * Push-канал ответов ассистента.
 *
 * Ответы агента приходят асинхронно, и до появления сокета единственным
 * способом их увидеть был периодический опрос истории. Сокет заменяет опрос,
 * но не отменяет его: прокси может не пропускать upgrade, и тогда приложение
 * обязано работать по-старому. Поэтому соединение сообщает о своём состоянии,
 * а вызывающий код решает, нужен ли запасной опрос.
 *
 * Соединение аутентифицируется cookie `sid` — она уходит автоматически при
 * подключении на тот же домен. Идентификатор сессии не передаётся ни в адресе,
 * ни в сообщениях.
 */

export interface AiSocketMessage {
  sessionId: string;
  text: string;
  attachment: AiChatAttachment | null;
}

export interface ChatSocketHandlers {
  /** Пришёл ответ ассистента. */
  onMessage(message: AiSocketMessage): void;
  /** Соединение установлено или восстановлено: историю нужно перечитать. */
  onReady(reconnected: boolean): void;
  /** Изменилось состояние канала: по нему решается судьба запасного опроса. */
  onAliveChange(alive: boolean): void;
}

export interface ChatSocket {
  close(): void;
}

/** Задержки переподключения: частые попытки подряд только мешают. */
const RETRY_DELAYS_MS = [1000, 3000, 7000, 15000];

function socketUrl(): string {
  const base = (import.meta.env.VITE_AUTH_BASE_URL as string | undefined)?.trim();

  if (base && base !== 'same-origin') {
    // Отладка с другого домена: cookie туда браузер не отправит, но адрес
    // собрать всё равно нужно корректно
    return `${base.replace(/^http/, 'ws').replace(/\/$/, '')}/api/miniapp/ws`;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${window.location.host}/api/miniapp/ws`;
}

export function connectChatSocket(handlers: ChatSocketHandlers): ChatSocket {
  if (!isBackendConfigured || typeof WebSocket === 'undefined') {
    return { close: () => undefined };
  }

  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let everConnected = false;
  let retryTimer: number | undefined;

  const open = () => {
    if (closed) return;

    try {
      socket = new WebSocket(socketUrl());
    } catch {
      scheduleRetry();
      return;
    }

    socket.onmessage = (event) => {
      let payload: { type?: string; session_id?: string; text?: string; attachment?: AiChatAttachment | null };
      try {
        payload = JSON.parse(String(event.data));
      } catch {
        return;
      }

      if (payload.type === 'ready') {
        attempt = 0;
        handlers.onAliveChange(true);
        handlers.onReady(everConnected);
        everConnected = true;
        return;
      }

      if (payload.type === 'ai_message') {
        handlers.onMessage({
          sessionId: payload.session_id ?? '',
          text: payload.text ?? '',
          attachment: payload.attachment ?? null,
        });
      }
    };

    socket.onclose = () => {
      handlers.onAliveChange(false);
      scheduleRetry();
    };

    // Ошибка сама по себе ничего не меняет: за ней всегда следует close
    socket.onerror = () => undefined;
  };

  const scheduleRetry = () => {
    if (closed) return;
    const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
    attempt += 1;
    retryTimer = window.setTimeout(open, delay);
  };

  open();

  return {
    close() {
      closed = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      // Обработчики снимаются до закрытия, иначе onclose запустит переподключение
      if (socket) {
        socket.onclose = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.close();
      }
    },
  };
}
